import { assertObject } from '../../validators';
import { WordElementSprite } from './WordElementSprite';
import { WordElementTexture } from './WordElementTexture';
import type { WordElementToken } from './WordElementToken';
import { WordElementMetrics } from './WordElementMetrics';

/**
 * This interface is used to store a cache entry in WordElementDataStore.
 */
interface WordElementCacheEntry {
    wordElementMetricsList: WordElementMetrics[];
    wordElementSpriteList: WordElementSprite[];
    wordElementTexture: WordElementTexture | null;
};

/**
 * This interface is used to count how many WordElementMetrics and WordElementSprites are required[1]
 * and issued (getNextWordElementSprite, getNextWordElementMetrics) in a cycle (since last reset).
 *
 * [1] The number of required WordElementMetrics and WordElementSprites depends on how many times addItem
 *     has been called for a WordElementToken. WordElementSprites can be cancelled with cancelWordElementSprite
 *     in case they are no longer needed. We need as many WordElementMetrics as we have the word element
 *     in the text, because each will have a different position (x,y).
 */
interface WordElementReferenceCount {
    totalRequiredMetrics: number;
    totalRequiredSprites: number;
    totalIssuedWordElements: number;
    totalIssuedMetrics: number;
};

/**
 * Generate default reference count which assumes one WordElement and one WordElementMetrics will be required.
 */
const getDefaultReferenceCount = (): WordElementReferenceCount => {
    return {
        totalRequiredMetrics: 1,
        totalRequiredSprites: 1,
        totalIssuedWordElements: 0,
        totalIssuedMetrics: 0,
    };
};

/**
 * This class is used to store WordElementMetrics and WordElements to reuse them when rendering words.
 */
export default class WordElementDataStore {
    readonly classType: string = 'WordElementDataStore';

    /**
     * Map of WordElementMetrics by it's key.
     */
    #wordElementDataMap: Map<string, WordElementCacheEntry> = new Map<string, WordElementCacheEntry>();
    /**
     * Map used to keep track of every word element added in a cycle
     */
    #itemReferenceCount: Map<string, WordElementReferenceCount> = new Map<string, WordElementReferenceCount>();

    #referenceToOutputMultiplier: number;

    constructor(referenceToOutputMultiplier = 1) {
        this.#referenceToOutputMultiplier = referenceToOutputMultiplier;
    }

    /**
     * Add a WordElementMetrics
     */
    addItem(wordElementToken: WordElementToken) {
        const key: string = this._buildKey(wordElementToken).valueOf();
        const referenceCount: WordElementReferenceCount = this._increment(key);
        let cacheEntry: WordElementCacheEntry = null;

        if (!(this.#wordElementDataMap.has(key))) {
            // create cache entry
            cacheEntry = this._buildCacheEntry(wordElementToken);
            this.#wordElementDataMap.set(key, cacheEntry);
        }
        else {
            cacheEntry = this.#wordElementDataMap.get(key);
        }

        if (referenceCount.totalRequiredMetrics > cacheEntry.wordElementMetricsList.length) {
            // we don't have enough metrics from previous runs
            // we'll generate new WordElementMetrics based on the first metrics
            const sourceWordElementMetrics = cacheEntry.wordElementMetricsList[0];

            const wordElementMetrics = new WordElementMetrics(
                sourceWordElementMetrics.text,
                sourceWordElementMetrics.font,
                sourceWordElementMetrics.style,
                {
                    boundingBox: sourceWordElementMetrics.boundingBox,
                    advanceWidth: sourceWordElementMetrics.advanceWidth,
                    wordElementToken: sourceWordElementMetrics.wordElementToken,
                });
            cacheEntry.wordElementMetricsList.push(wordElementMetrics);
        }
    }

    /**
     * Adds many WordElementMetrics
     */
    addItems(wordElementTokens: WordElementToken[]) {
        wordElementTokens.forEach(wordElementToken => {
            this.addItem(wordElementToken);
        });
    }

    /**
     * Verify if we have the metrics for a specific wordElementToken.
     */
    hasItem(wordElementToken: WordElementToken): boolean {
        const key: string = this._buildKey(wordElementToken).valueOf();
        return this.#wordElementDataMap.has(key);
    }

    /**
     * Begin counting cache entry usage.
     * Every item added before calling this method will be removed by prune().
     */
    resetCount() {
        for (const key of this.#itemReferenceCount.keys()) {
            const referenceCount = this.#itemReferenceCount.get(key);
            referenceCount.totalRequiredMetrics = 0;
            referenceCount.totalRequiredSprites = 0;
            referenceCount.totalIssuedWordElements = 0;
            referenceCount.totalIssuedMetrics = 0;
        }
    }

    /**
     * Cancel 1 item's WordElementSprite request.
     * This decrements the number of requested WordElementSprites for this WordElementToken.
     * Call this method when you determine that a previously added token will not need a WordElementSprite.
     */
    cancelWordElementSprite(wordElementToken: WordElementToken) {
        const key: string = this._buildKey(wordElementToken).valueOf();
        const referenceCount = this.#itemReferenceCount.get(key);
        if (referenceCount === null) {
            throw new Error('No reference count found for this token.');
        }
        if (referenceCount.totalRequiredSprites === 0) {
            throw new Error('Attempt to cancel a WordElementSprite which has been entirely cancelled.');
        }
        referenceCount.totalRequiredSprites = referenceCount.totalRequiredSprites - 1;
    }

    /**
     * Delete unused cached item.
     * @return Object containing deletion statistics
     */
    prune(): Object {
        let deletedEntriesCount = 0;
        let deletedMetricsCount = 0;
        let deletedWordElementsCount = 0;
        for (const key of this.#wordElementDataMap.keys()) {
            const referenceCount = this.#itemReferenceCount.get(key);
            const cacheEntry = this.#wordElementDataMap.get(key);

            if (!(this.#itemReferenceCount.has(key)) || referenceCount.totalRequiredMetrics === 0) {
                // token is not used at all anymore; remove whole cache entry
                if (cacheEntry.wordElementSpriteList.length > 0) {
                    const wordElementTexture = cacheEntry.wordElementSpriteList[0];
                    if (!wordElementTexture.destroyed) {
                        wordElementTexture.destroy();
                    }
                }
                this.#wordElementDataMap.delete(key);
                this.#itemReferenceCount.delete(key);
                deletedEntriesCount++;
            }
            else {
                // Remove unused WordElementSprites
                while (cacheEntry.wordElementSpriteList.length > referenceCount.totalRequiredSprites) {
                    if (cacheEntry.wordElementSpriteList.length === 1) {
                        // This is the last WordElementSprite using the texture, so we delete it now.
                        cacheEntry.wordElementSpriteList[0].destroy();
                    }
                    cacheEntry.wordElementSpriteList.shift();
                    if (referenceCount.totalIssuedWordElements > 0) {
                        referenceCount.totalIssuedWordElements--;
                    }
                    deletedWordElementsCount++;
                }
                // Remove unused WordElementTextures
                if (referenceCount.totalRequiredSprites === 0 && cacheEntry.wordElementTexture !== null) {
                    cacheEntry.wordElementTexture.destroy();
                    cacheEntry.wordElementTexture = null;
                }

                // Remove unused metrics
                // Note that we always keep at least one metrics to be able to clone it later
                while (cacheEntry.wordElementMetricsList.length > Math.max(referenceCount.totalRequiredMetrics, 1)) {
                    cacheEntry.wordElementMetricsList.shift();
                    referenceCount.totalIssuedMetrics--;
                    deletedMetricsCount++;
                }
            }
        }
        return { deletedEntriesCount, deletedMetricsCount, deletedWordElementsCount };
    }

    /**
     * Generate as many WordElementSprites as required according to the reference count.
     * Make sure you eventually call destroy() if you call this method.
     * @return The number of generated WordElementSprites
     */
    generateRequiredSprites() {
        for (const key of this.#itemReferenceCount.keys()) {
            const totalRequiredSprites = this.#itemReferenceCount.get(key).totalRequiredSprites;
            const cacheEntry = this.#wordElementDataMap.get(key);

            // Generate the texture once for all word elements in the text which are identical (except for their position)
            if (totalRequiredSprites > 0 && cacheEntry.wordElementTexture === null) {
                const metrics = cacheEntry.wordElementMetricsList[0];
                cacheEntry.wordElementTexture = new WordElementTexture(metrics, this.#referenceToOutputMultiplier);
                cacheEntry.wordElementTexture.updateText();
            }

            // Generate enough sprites to display the texture as many times as it appears in the text.
            const wordElementSpriteList = cacheEntry.wordElementSpriteList;
            while (wordElementSpriteList.length < totalRequiredSprites) {
                const sprite = new WordElementSprite(cacheEntry.wordElementTexture);
                wordElementSpriteList.push(sprite);
            }
        }
    }

    /**
     * Gets next unissued WordElement since last reset for this WordElementToken
     */
    getNextWordElementSprite(wordElementToken: WordElementToken): WordElementSprite {
        const key: string = this._buildKey(wordElementToken).valueOf();
        const referenceCount = this.#itemReferenceCount.get(key);
        const cacheEntry = this.#wordElementDataMap.get(key);

        if (!referenceCount) {
            throw Error(`Trying to get a key which was never added in WordElementDataStore: ${key}`);
        }

        if (referenceCount.totalIssuedWordElements >= referenceCount.totalRequiredSprites) {
            throw Error(`Not enough wordElements were generated for this token ${key}`);
        }

        if (referenceCount.totalRequiredSprites !== cacheEntry.wordElementSpriteList.length) {
            throw Error(`Wrong amount of textures generated for this token ${key} got ${cacheEntry.wordElementSpriteList.length}, but expected ${referenceCount.totalRequiredSprites}`);
        }

        return cacheEntry.wordElementSpriteList[referenceCount.totalIssuedWordElements++];
    }

    /**
     * Gets next unissued WordElementMetrics since last reset for this WordElementToken
     */
    getNextWordElementMetrics(wordElementToken: WordElementToken): WordElementMetrics {
        const key: string = this._buildKey(wordElementToken).valueOf();
        const referenceCount = this.#itemReferenceCount.get(key);
        const cacheEntry = this.#wordElementDataMap.get(key);
        const wordElementMetricsList = cacheEntry.wordElementMetricsList;

        if (referenceCount.totalIssuedMetrics >= referenceCount.totalRequiredMetrics) {
            throw Error(`Not enough wordElementsMetrics were generated for this token ${key}.`);
        }

        if (referenceCount.totalRequiredMetrics > cacheEntry.wordElementMetricsList.length) {
            throw Error(`Wrong amount of metrics generated for this token ${key}`);
        }

        return wordElementMetricsList[referenceCount.totalIssuedMetrics++];
    }

    /**
     * Destroys all WordElements.
     */
    destroy() {
        for (const key of this.#wordElementDataMap.keys()) {
            const cacheEntry = this.#wordElementDataMap.get(key);
            while (cacheEntry.wordElementSpriteList.length > 0) {
                if (!cacheEntry.wordElementSpriteList[0].destroyed &&
                    cacheEntry.wordElementSpriteList.length === 1) {
                    // This is the last WordElement using the texture, so we delete it now.
                    cacheEntry.wordElementSpriteList[0].destroy();
                }
                // Delete the wordElement
                cacheEntry.wordElementSpriteList.shift();
            }
            cacheEntry.wordElementMetricsList.splice(0);
            if (cacheEntry.wordElementTexture !== null) {
                cacheEntry.wordElementTexture.destroy();
                cacheEntry.wordElementTexture = null;
            }
        }
    }

    /**
     * Returns the number of items in the cache.
     * This is meant only to be used in tests.
     * @ignore
     * @internal
     */
    get _size(): number {
        return this.#wordElementDataMap.size;
    }

    /**
     * Returns the total number of WordElementSprites in the cache.
     * This is meant only to be used in tests.
     * @ignore
     * @internal
     */
    get _totalWordElementSprites(): number {
        let totalWordElementSprites = 0;

        for (const key of this.#wordElementDataMap.keys()) {
            const cacheEntry = this.#wordElementDataMap.get(key);
            totalWordElementSprites += cacheEntry.wordElementSpriteList.length;
        }
        return totalWordElementSprites;
    }

    /**
     * Increment usage count for a given key in the itemReferenceCount.
     */
    private _increment(key: string): WordElementReferenceCount {
        let currentReferenceCount = null;
        if (!(this.#itemReferenceCount.has(key))) {
            currentReferenceCount = getDefaultReferenceCount();
            this.#itemReferenceCount.set(key, currentReferenceCount);
        }
        else {
            currentReferenceCount = this.#itemReferenceCount.get(key);
            // Create an additionnal metrics to be attached to the texture
            currentReferenceCount.totalRequiredMetrics += 1;
            currentReferenceCount.totalRequiredSprites += 1;
        }
        return currentReferenceCount;
    }

    /**
     * Builds a key uniquely identifying a WordElementToken
     */
    private _buildKey(wordElementToken: WordElementToken): String {
        assertObject(wordElementToken.font);
        assertObject(wordElementToken.font.font);
        assertObject(wordElementToken.style);

        return [
            wordElementToken.font.familyName,
            wordElementToken.highlighted,
            wordElementToken.text,
            wordElementToken.style.align,
            wordElementToken.style.dropShadow,
            wordElementToken.style.dropShadowAlpha,
            wordElementToken.style.dropShadowAngle,
            wordElementToken.style.dropShadowBlur,
            wordElementToken.style.dropShadowColor,
            wordElementToken.style.dropShadowDistance,
            wordElementToken.style.fill,
            wordElementToken.style.fontSize,
            wordElementToken.style.fontUrl,
            wordElementToken.style.leading,
            wordElementToken.style.maxTextHeight,
            wordElementToken.style.readingDirection,
            wordElementToken.style.stroke,
            wordElementToken.style.strokeThickness,
        ].join('-');
    }

    /**
     * Creates cached data for a wordElementToken.
     */
    private _buildCacheEntry(wordElementToken): WordElementCacheEntry {
        assertObject(wordElementToken.font);

        const wordElementMetrics = new WordElementMetrics(
            wordElementToken.text,
            wordElementToken.font.font,
            wordElementToken.style,
            {
                fontUrl: wordElementToken.font.sourceUrl,
                wordElementToken,
            }
        );

        return {
            wordElementMetricsList: [wordElementMetrics],
            wordElementSpriteList: [],
            wordElementTexture: null,
        };
    }
}
